import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

import {
  districtCreateSchema,
  districtUpdateSchema,
} from '../../schemas';
import { DistrictCreateFormData, DistrictUpdateFormData , District } from '../../types';
import { Button } from '../common';
import { Input } from '../forms';

interface DistrictFormProps {
  district?: District;
  onSubmit: (data: DistrictCreateFormData | DistrictUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const DistrictForm: React.FC<DistrictFormProps> = ({
  district,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const isEdit = !!district;
  const schema = isEdit ? districtUpdateSchema : districtCreateSchema;

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: district
      ? {
          name: district.name,
          code: district.code,
          state: district.state,
          country: district.country,
          city: district.city || '',
          zip_code: district.zip_code || '',
          superintendent: district.superintendent || '',
          email: district.email || '',
          phone: district.phone || '',
          address: district.address || '',
          website: district.website || '',
        }
      : {
          name: '',
          code: '',
          state: '',
          country: '',
          city: '',
          zip_code: '',
          superintendent: '',
          email: '',
          phone: '',
          address: '',
          website: '',
        },
  });

  const handleFormSubmit = (
    data: DistrictCreateFormData | DistrictUpdateFormData
  ) => {
    // Convert empty strings to undefined for optional fields
    const cleanedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        value === '' ? undefined : value,
      ])
    );
    onSubmit(cleanedData);
  };

  return (
    <div className="h-full flex flex-col">
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="h-full flex flex-col"
      >
        {/* Scrollable form content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* District Name */}
              <div>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="District Name *"
                      placeholder="Enter district name"
                      error={errors.name?.message}
                    />
                  )}
                />
              </div>

              {/* District Code */}
              <div>
                <Controller
                  name="code"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="District Code *"
                      placeholder="e.g., ABC123"
                      error={errors.code?.message}
                    />
                  )}
                />
              </div>

              {/* State */}
              <div>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="State *"
                      placeholder="e.g., California"
                      error={errors.state?.message}
                    />
                  )}
                />
              </div>

              {/* Country */}
              <div>
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="Country *"
                      placeholder="e.g., United States"
                      error={errors.country?.message}
                    />
                  )}
                />
              </div>

              {/* City */}
              <div>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="City"
                      placeholder="e.g., Los Angeles"
                      error={errors.city?.message}
                    />
                  )}
                />
              </div>

              {/* Zip Code */}
              <div>
                <Controller
                  name="zip_code"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="Zip Code"
                      placeholder="e.g., 90210"
                      error={errors.zip_code?.message}
                    />
                  )}
                />
              </div>

              {/* Superintendent */}
              <div>
                <Controller
                  name="superintendent"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="Superintendent"
                      placeholder="Enter superintendent name"
                      error={errors.superintendent?.message}
                    />
                  )}
                />
              </div>

              {/* Email */}
              <div>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      type="email"
                      label="Email"
                      placeholder="superintendent@district.edu"
                      error={errors.email?.message}
                    />
                  )}
                />
              </div>

              {/* Phone */}
              <div>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      type="tel"
                      label="Phone"
                      placeholder="(555) 123-4567"
                      error={errors.phone?.message}
                    />
                  )}
                />
              </div>

              {/* Website */}
              <div>
                <Controller
                  name="website"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      type="url"
                      label="Website"
                      placeholder="https://www.district.edu"
                      error={errors.website?.message}
                    />
                  )}
                />
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="Address"
                      placeholder="Enter full address"
                      error={errors.address?.message}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions - Sticky at bottom */}
        <div className="flex-shrink-0 flex justify-end space-x-3 p-6 pt-4 border-t border-gray-200 bg-white">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting || isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || isLoading}
            loading={isSubmitting || isLoading}
          >
            {isEdit ? 'Update District' : 'Create District'}
          </Button>
        </div>
      </form>
    </div>
  );
};
